import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid } from "recharts";
import Layout from "../components/Layout";
import CircularProgress from "../components/CircularProgress";
import { api } from "../lib/api";

export default function ProgressPage() {
  const [dash, setDash] = useState(null);
  const [scans, setScans] = useState([]);
  const [checkins, setCheckins] = useState([]);

  useEffect(() => {
    api.get("/me/dashboard").then((r) => setDash(r.data));
    api.get("/pain-scans").then((r) => setScans(r.data));
    api.get("/daily-checkin").then((r) => setCheckins(r.data));
  }, []);

  const scanSeries = scans.map((s, i) => ({
    label: `Step ${s.step}`,
    intensity: s.average_intensity,
    idx: i + 1,
  }));

  const checkinSeries = checkins.map((c) => ({
    date: c.date.slice(5),
    pain: c.pain_score,
    emotion: c.emotional_score,
    alignment: c.alignment_score,
  }));

  return (
    <Layout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="bps-eyebrow">Progress</div>
        <h1 className="font-display text-3xl font-light tracking-tighter mt-1">Your reset, visualized.</h1>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="bps-card p-5 flex items-center gap-4" data-testid="progress-completion">
            <CircularProgress value={dash?.completion_pct || 0} size={80} color="#22D3EE" />
            <div>
              <div className="bps-eyebrow">Completion</div>
              <div className="font-display text-lg mt-1">{dash?.exercises_done || 0}/16</div>
            </div>
          </div>
          <div className="bps-card p-5" data-testid="progress-streak">
            <div className="bps-eyebrow">Streak</div>
            <div className="font-display text-3xl mt-1">{dash?.streak || 0}</div>
            <div className="text-xs text-[#7C8FA8]">consecutive days</div>
          </div>
        </div>

        <div className="mt-5 bps-card p-5">
          <div className="bps-eyebrow mb-3">Pain trend (body scans)</div>
          {scanSeries.length > 0 ? (
            <div className="h-48" data-testid="pain-trend">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={scanSeries}>
                  <defs>
                    <linearGradient id="pain" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F87171" stopOpacity={0.55}/>
                      <stop offset="100%" stopColor="#F87171" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#7C8FA8" fontSize={11} />
                  <YAxis stroke="#7C8FA8" fontSize={11} domain={[0, 10]} />
                  <Tooltip contentStyle={{ background: "#0A1530", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#F5F7FA" }} />
                  <Area type="monotone" dataKey="intensity" stroke="#F87171" strokeWidth={2} fill="url(#pain)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-[#9AAEC6]">Complete your first body scan to see your trend.</p>
          )}
        </div>

        <div className="mt-5 bps-card p-5">
          <div className="bps-eyebrow mb-3">Daily check-ins</div>
          {checkinSeries.length > 0 ? (
            <div className="h-56" data-testid="checkin-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={checkinSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" stroke="#7C8FA8" fontSize={11} />
                  <YAxis stroke="#7C8FA8" fontSize={11} domain={[0, 10]} />
                  <Tooltip contentStyle={{ background: "#0A1530", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#F5F7FA" }} />
                  <Line type="monotone" dataKey="pain" stroke="#F87171" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="emotion" stroke="#22D3EE" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="alignment" stroke="#10B981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-[#9AAEC6]">Save a daily check-in on the home screen to start tracking.</p>
          )}
          <div className="mt-3 flex gap-4 text-[11px] text-[#7C8FA8]">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F87171]"/>Pain</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#22D3EE]"/>Emotion</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981]"/>Alignment</span>
          </div>
        </div>
      </motion.div>
    </Layout>
  );
}
